import { CSSProperties, FC } from "react";
import { useFormContext, UseFormMethods, useWatch } from "react-hook-form";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { FormType } from "../FarmFlow/common";
import { useIntl } from "../../../../_metronic/i18n/customUseIntl";
import {
  processAndStageStylesV2,
  DSM_GREY,
} from "../../../../_metronic/layout";
import ReactHookDsmInput from "../../../modules/Helpers/ReactHookDsmInput2";
import { UserProfilePrefs } from "../../../modules/Helpers/UserProfilePrefs";
import { SlaughterhouseFacilityForm } from "../../models/Facility/FacilityTypes";
import { resolvePath } from "../../../modules/Helpers/resolvePathFn";

interface FacilityShrimpProductInputsProps {
  formType: FormType;
  methods: UseFormMethods<SlaughterhouseFacilityForm>;
}

type DsmTypes = any;

const FacilityShrimpProductInputs: FC<FacilityShrimpProductInputsProps> = ({
  formType,
  methods,
}) => {
  const intl = useIntl();
  //const formContext = useFormContext();
  const classes: DsmTypes = processAndStageStylesV2();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const userUOM = UserProfilePrefs.getInstance().getUserUnitPrefs();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const outputMassUnit = userUOM.unitBarnOutputMass;
  const formContext = useFormContext();
  const boldTextStyle = {
    fontWeight: "bold",
    fontSize: "18px",
    color: "black",
    margin: "24px 16px 0 16px",
    padding: 0,
  };

  const frozenWatch = useWatch({
    name: "frozen",
  });
  const refrigeratedWatch = useWatch({
    name: "refrigerated",
  });
  const ambientWatch = useWatch({
    name: "ambient",
  });

  const getFrozenValue = (): number => (frozenWatch ? Number(frozenWatch) : 0);

  const getRefrigeratedValue = (): number =>
    refrigeratedWatch ? Number(refrigeratedWatch) : 0;

  const getAmbientValue = (): number =>
    ambientWatch ? Number(ambientWatch) : 0;

  const handleCoolingType = async () => {
    if (!frozenWatch && refrigeratedWatch && ambientWatch) {
      let value = getFrozenValue() + getRefrigeratedValue() + getAmbientValue();

      value = 100 - value;

      methods.setValue("frozen", value > 0 ? value : 0);
    }

    if (frozenWatch && !refrigeratedWatch && ambientWatch) {
      let value = getFrozenValue() + getRefrigeratedValue() + getAmbientValue();

      value = 100 - value;

      methods.setValue("refrigerated", value > 0 ? value : 0);
    }

    if (frozenWatch && refrigeratedWatch && !ambientWatch) {
      let value = getFrozenValue() + getRefrigeratedValue() + getAmbientValue();

      value = 100 - value;

      methods.setValue("ambient", value > 0 ? value : 0);
    }

    const total = getFrozenValue() + getRefrigeratedValue() + getAmbientValue();

    if (total === 100) {
      if (!frozenWatch) methods.setValue("frozen", 0);
      if (!refrigeratedWatch) methods.setValue("refrigerated", 0);
      if (!ambientWatch) methods.setValue("ambient", 0);
    }

    await methods.trigger("frozen");
    await methods.trigger("refrigerated");
    await methods.trigger("ambient");
  };

  const renderAdditionText = () => <span style={boldTextStyle}>+</span>;

  const isTotalWeightPercentageError = () =>
    Boolean(
      resolvePath(formContext.errors, `frozen`, null)
    );

  const getTotalText = () => {
    const totalTimeSpentTextStyle = isTotalWeightPercentageError()
      ? { color: "var(--dsm-color-error)", fontWeight: "500" }
      : ({ color: "var(--dsm-color-basic-black)" } as CSSProperties);

    const totalText = intl.formatMessage({
      id: "SUSTELL.FACILITIES.INFO.TOTAL_WEIGHT_PRD",
    });
    const errorHelperText = intl.formatMessage({
      id: "SUSTELL.FACILITIES.TOTAL_WEIGHT_PRD.SUM_MUST_BE_100",
    });
    const totalPercentageText = "= 100%";

    const mountedTextElement = (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <span
          style={{
            fontWeight: "bold",
            color: "var(--dsm-color-basic-black)",
            fontSize: "18px",
          }}
        >
          {totalPercentageText}
        </span>
        <span
          style={{
            ...totalTimeSpentTextStyle
          }}
        >
          ({totalText})
        </span>
      </div>
    );

    if (isTotalWeightPercentageError()) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            paddingBottom: "15px",
          }}
        >
          <div style={{ display: "flex" }}>
            {mountedTextElement}
          </div>
          <span style={{ color: "var(--dsm-color-error)", fontSize: "10px", marginTop: "4px" }}>
            {errorHelperText}
          </span>
        </div>
      );
    }

    return mountedTextElement;
  };

  return (
    <>
      <DsmGrid
        className={classes.dsmGridTwoColumnFacilities}
        style={{ marginTop: `var(--dsm-spacing-px-6)` }}
      >
        <DsmGrid
          className={classes.additionalEntriesFacilitiesBlockHolder}
          style={{
            borderRight: "0",
            backgroundColor: DSM_GREY,
            grid: "auto/1fr",
          }}
        >
          <div className={classes.facilityFieldContainer}>
            <ReactHookDsmInput
              name="totalProductWeight"
              label={intl.formatMessage({
                id: "SUSTELL.FACILITIES.INFO.TOTAL_PRODUCT_LW",
              })}
              required
              type="number"
              adornment={intl.formatMessage(
                {
                  id: "SUSTELL.FACILITIES.RESOURCES.UNIT_PER_YEAR_PLACEHOLDER",
                },
                { unit: outputMassUnit }
              )}
              tooltip={intl.formatMessage({
                id: "SUSTELL.FACILITIES.INFO.TOTAL_PRODUCT_LW.TT",
              })}
              disabled={formType === FormType.View}
            />
          </div>
        </DsmGrid>
        <DsmGrid
          className={classes.additionalEntriesFacilitiesBlockHolder}
          style={{ grid: "auto/0.8fr 1fr 1fr 1fr" }}
        >
          <div className={classes.facilityFieldContainer}>
            <ReactHookDsmInput
              name="frozen"
              label={intl.formatMessage({
                id: "SUSTELL.FACILITIES.INFO.FROZEN",
              })}
              required
              type="number"
              adornment="%"
              tooltip={intl.formatMessage({
                id: "SUSTELL.FACILITIES.INFO.FROZEN.TT",
              })}
              disabled={formType === FormType.View}
              focusLostHandler={handleCoolingType}
              showHelperText={false}
            />
          </div>
          <div className={classes.facilityFieldContainer}>
            {renderAdditionText()}
            <ReactHookDsmInput
              name="refrigerated"
              label={intl.formatMessage({
                id: "SUSTELL.FACILITIES.INFO.REFRIGERATED",
              })}
              required
              type="number"
              adornment="%"
              tooltip={intl.formatMessage({
                id: "SUSTELL.FACILITIES.INFO.REFRIGERATED.TT",
              })}
              disabled={formType === FormType.View}
              focusLostHandler={handleCoolingType}
              showHelperText={false}
            />
          </div>
          <div className={classes.facilityFieldContainer}>
            {renderAdditionText()}
            <ReactHookDsmInput
              name="ambient"
              label={intl.formatMessage({
                id: "SUSTELL.FACILITIES.INFO.AMBIENT_TEMPERATURE",
              })}
              required
              type="number"
              adornment="%"
              tooltip={intl.formatMessage({
                id: "SUSTELL.FACILITIES.INFO.AMBIENT_TEMPERATURE.TT",
              })}
              disabled={formType === FormType.View}
              focusLostHandler={handleCoolingType}
              showHelperText={false}
            />
          </div>
          <div style={{ marginLeft: "var(--dsm-spacing-px-8)" }}>
            {getTotalText()}
          </div>
        </DsmGrid>
      </DsmGrid>
      <br />
    </>
  );
};

export default FacilityShrimpProductInputs;
